import React, { useState, useRef, useEffect } from 'react';
import { VideoRequest } from '../types/video';

interface ScenarioInputProps {
  scenarioDescription: string;
  onScenarioChange: (value: string) => void;
  onGenerateVideo?: (scenarioDescription: string) => void;
  currentVideo: VideoRequest | null;
  isBusy?: boolean;
}

interface Template {
  id: string;
  name: string;
  description: string;
  summary: string;
}

const TEMPLATES: Template[] = [
  {
    id: 'rotonde',
    name: 'Rotonde',
    description:
      'Een auto nadert een rotonde en moet een fiets met voorrang laten gaan. De auto remt en wacht tot de fietser is overgestoken.',
    summary: 'Auto laat fietser voorgaan op rotonde',
  },
  {
    id: 'zebrapad',
    name: 'Zebrapad',
    description:
      'Een fietser nadert een zebrapad en steekt over. Een naderende auto ziet de fietser en remt tijdig om voorrang te verlenen.',
    summary: 'Fietser steekt veilig over bij zebrapad',
  },
  {
    id: 'kruispunt',
    name: 'Kruispunt',
    description:
      "Twee auto's komen tegelijk aan een kruispunt zonder verkeerslichten. De auto van rechts heeft voorrang en gaat eerst.",
    summary: 'Auto van rechts neemt voorrang op kruispunt',
  },
  {
    id: 'voetganger',
    name: 'Voetganger',
    description:
      'Een voetganger wil oversteken bij een zebrapad. Een naderende auto stopt tijdig om de voetganger veilig over te laten steken.',
    summary: 'Voetganger steekt over bij attent voertuig',
  },
  {
    id: 'snelweg',
    name: 'Snelweg',
    description:
      'Een auto wil invoegen op de snelweg. De bestuurder kijkt in de spiegels, versnelt en voegt veilig in tussen het overige verkeer.',
    summary: 'Auto voegt gecontroleerd in op snelweg',
  },
  {
    id: 'parallelweg',
    name: 'Parallelweg',
    description:
      'Een fietser rijdt op een parallelweg en moet oversteken naar het hoofdfietspad. Er komt een auto aan vanaf rechts.',
    summary: 'Fietser kruist drukke parallelweg',
  },
];

interface TemplateDropdownProps {
  templates: Template[];
  selectedId: string | null;
  onSelect: (template: Template) => void;
  disabled?: boolean;
}

const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
  templates,
  selectedId,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedTemplate = templates.find(template => template.id === selectedId) || null;

  useEffect(() => {
    if (!isOpen) return;

    const handleClickAway = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="dropdown w-full text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex-1 truncate">
          {selectedTemplate ? selectedTemplate.name : 'Kies een template'}
        </span>
        <svg
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute top-full left-0 right-0 mt-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg z-50 max-h-64 overflow-y-auto"
        >
          <div className="py-1">
            {templates.map(template => (
              <button
                key={template.id}
                type="button"
                role="option"
                aria-selected={selectedId === template.id}
                onClick={() => {
                  onSelect(template);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left transition-colors flex items-start gap-3 ${
                  selectedId === template.id
                    ? 'bg-[var(--color-muted)] text-[var(--color-primary)]'
                    : 'hover:bg-[var(--color-muted)]'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                    {template.name}
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)] truncate">
                    {template.summary}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ScenarioInput: React.FC<ScenarioInputProps> = ({
  scenarioDescription,
  onScenarioChange,
  onGenerateVideo,
  currentVideo,
  isBusy = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const isProcessing = currentVideo?.status === 'processing';
  const isDisabled = isBusy || isProcessing;

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }

    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [scenarioDescription]);

  useEffect(() => {
    const trimmed = scenarioDescription.trim();
    const matchingTemplate = TEMPLATES.find(template => template.description === trimmed);

    setSelectedTemplateId(prev => {
      const next = matchingTemplate?.id ?? null;
      return prev === next ? prev : next;
    });
  }, [scenarioDescription]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    onScenarioChange(value);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplateId(template.id);
    onScenarioChange(template.description);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      const trimmed = scenarioDescription.trim();

      if (trimmed.length > 0 && onGenerateVideo && !isDisabled) {
        onGenerateVideo(trimmed);
      }
    }
  };

  const characterCount = scenarioDescription.length;
  const maxCharacters = 500;

  return (
    <div className="card p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Beschrijf verkeerssituatie
        </h2>
      </div>

      {/* Scenario Input */}
      <div className="space-y-3">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={scenarioDescription}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Beschrijf de verkeerssituatie die je wilt omzetten naar een video..."
            className="input-field w-full h-32 resize-none pr-12 overflow-hidden"
            disabled={isDisabled}
            maxLength={maxCharacters}
          />

          {/* Upload/confirm button */}
          <button
            className={`confirm-button absolute bottom-3 right-3 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isDisabled}
            title="Bevestig scenario of upload referentie"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v8m0-8l4 4m-4-4l-4 4M16 6a4 4 0 10-8 0v6a4 4 0 108 0V6z" />
            </svg>
            <span className="sr-only">Bevestig scenario of upload referentie</span>
          </button>
        </div>

        {/* Character counter */}
        <div className="text-xs text-[var(--color-text-secondary)] text-right">
          {characterCount}/{maxCharacters} karakters
        </div>
      </div>

      {/* Templates */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
            Templates
          </h3>
        </div>

        <TemplateDropdown
          templates={TEMPLATES}
          selectedId={selectedTemplateId}
          onSelect={handleTemplateSelect}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};

export default ScenarioInput;
